import React, { useState, useEffect, useContext, useRef } from 'react';
import ModalEliminacion from '../modal/ModalEliminacion';
import { AuthContext } from '../contexts/AuthContext';
import { Get_type_code_options } from '../Comun';
import { apiFetch } from '../utils/apiFetch';
import { notifySuccess, notifyError } from '../utils/toast';


interface Tag 
{
    id: number;
    tag: string;
    valid_time: string;
    type_tag: number;
}

interface CodeProps 
{
  code?: 
  {
    id: number; 
    code: string;
    response: string;
    type: number;
    id_tag: number;
    number_of_uses: number;
    number_of_uses_used: number;    
  };

  id_event: number;

  tags: Tag[];

  onSave: (codeData: 
  {
    id?: number; 
    code: string;
    response: string;
    type: number;
    id_tag: number;
    number_of_uses: number;
    number_of_uses_used?: number; 
  }) => void;

  onDelete?: (codeId: number) => void;
  
  onCancel: () => void;
}

const Codigo: React.FC<CodeProps> = ({ code, id_event, tags, onSave, onDelete, onCancel }) => 
{
  const { token } = useContext(AuthContext);
  const [codeCode, setCode] = useState('');
  const [codeResponse, setResponse] = useState('');
  const [typeCode, setTypeCode] = useState<number>(-1);
  const [idTypeTag, setIdTypeTag] = useState<number>(-1);
  const [numberOfUses, setNumberOfUses] = useState<number | string>(0); // Permitir tanto número como string
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null); // Ref para el contenedor del modal  

   // Opciones para el selector
   const typeCodeOptions = Get_type_code_options();

  // Determinar si estamos en modo de edición o creación
  const isEditing = code && code.id !== -1;
    
  //-----------------------------------------------------------

  useEffect(() => 
  {
    if (code) 
    {
      setCode(code.code);
      setResponse(code.response);
      setTypeCode(code.type);
      setIdTypeTag(code.id_tag);
      setNumberOfUses(code.number_of_uses === -1 ? "Número de usos" : code.number_of_uses);
    } 
    else 
    {
      // Inicializa el estado para un nuevo tag
      setCode('');
      setResponse('');
      setTypeCode(-1);
      setIdTypeTag(-1);
      setNumberOfUses(-1);
    }
  }, [code]);

  //-----------------------------------------------------------

  useEffect(() => 
  {
    // Función para manejar clics fuera del componente
    const handleClickOutside = (event: MouseEvent) => 
    {
      if (modalRef.current && !modalRef.current.contains(event.target as Node))
        onCancel();
    };

    // Función para manejar presiones de teclas
    const handleEscPress = (event: KeyboardEvent) => 
    {
      if (event.key === 'Escape') 
        onCancel();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscPress);

    return () => 
    {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscPress);
    };
  }, [onCancel]);  

  //-----------------------------------------------------------
  
  const handleSubmit = async (e: React.FormEvent) => 
  {
    e.preventDefault();

    if (/^\s+|\s+$/g.test(codeCode)) 
    {
      setError('El código no puede tener espacios o tabulaciones al principio o al final.');
      return;
    }

    if (typeCode === -1) 
    {
        setError('Debe seleccionar un tipo válido.');
        return;
    }

    if (idTypeTag === -1) 
    {
        setError('Debe seleccionar un tag válido.');
        return;
    }

    try 
    {
      const formData = new URLSearchParams();

      if (code && code.id !== null)
        formData.append('id', code.id.toString());

      formData.append('id_event', id_event.toString());
      formData.append('type', typeCode.toString());
      formData.append('id_tag', idTypeTag.toString());
      formData.append('code', codeCode);
      formData.append('response', codeResponse);
      formData.append('number_of_uses', typeof numberOfUses === 'number' ? numberOfUses.toString() : ''); // Enviar como string
      
      const url = isEditing ? 'web/update_code' : 'web/create_code';

      const response = await apiFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'},
        body: formData.toString()});

      if (response.ok) 
      {
        onSave({
          id: code?.id ?? undefined, 
          code: codeCode, 
          response: codeResponse,
          type: typeCode,
          id_tag : idTypeTag,
          number_of_uses: typeof numberOfUses === 'number' ? numberOfUses : 0,
          number_of_uses_used: code?.number_of_uses_used ?? undefined}
        );
        notifySuccess('Código guardado correctamente');
      } 
      else 
      {
        const errorData = await response.json();
        setError(errorData.error || 'Error al guardar el código.');
      }
    } 
    catch (error) 
    {
      setError('Error de red. Por favor, intente nuevamente.');
    }
  };

  //-----------------------------------------------------------

  const handleDelete = () => 
  {
    if (code && code.id !== null && code.id !== -1) 
    {
      setIsModalOpen(true);
    }
  };

  //-----------------------------------------------------------

  const handleConfirmDelete = async () => 
  {
    if (code && code.id !== null && code.id !== -1 && onDelete) 
    {
      try 
      {
        const formData = new URLSearchParams();
        formData.append('id', code.id.toString());

        const response = await apiFetch(`web/delete_code`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'},
          body: formData.toString()});

        if (response.ok) 
        {
          onDelete(code.id);
          setIsModalOpen(false);
          notifySuccess('Código eliminado correctamente');
        } 
        else 
        {
          const errorData = await response.json();
          setError(errorData.error || 'Error al eliminar el código.');
        }
      } 
      catch (error) 
      {
        setError('Error de red. Por favor, intente nuevamente.');
      }
    }
  };

  //-----------------------------------------------------------

  // Manejar el cambio en el selector
  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => 
  {
    setTypeCode(Number(event.target.value));
  };

  //-----------------------------------------------------------

  const handleTagSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => 
  {
    setIdTypeTag(Number(event.target.value));
  };

  //-----------------------------------------------------------

  return (
    <div className="evento-container" ref={modalRef}>
      <button type="button" className="modal-close-button" onClick={onCancel} aria-label="Cerrar">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
        </svg>
      </button>
      <h2>{isEditing ? 'Editar Código' : 'Crear Código'}</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
            <input
            id="code"
            type="text"
            value={codeCode}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Código"
            maxLength={32}
            disabled={isEditing} // Solo lectura si estamos en edición
            required
            />
        </div>
        <div className="form-group">
            <input
                id="respuesta"
                type="text"
                value={codeResponse}
                onChange={(e) => setResponse(e.target.value)}
                placeholder="Respuesta"
                maxLength={64}
            />
        </div>
        <div className="form-row">
            <div className="form-group select-group">
                <select
                value={typeCode}
                onChange={handleSelectChange}
                required>
                <option value={-1}>Selecciona un tipo</option>
                {typeCodeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                    {option.label}
                    </option>
                ))}
                </select>
            </div>
            <div className="form-group select-group">
                <select
                    value={idTypeTag}
                    onChange={handleTagSelectChange}
                    required>
                    <option value={-1}>Selecciona un tag</option>
                    {tags.map(tag => (
                    <option key={tag.id} value={tag.id}>
                        {tag.tag}
                    </option>
                    ))}
                </select>
            </div>
            <div className="form-group">
            <input
                            id="id_number_of_uses"
                            type="text" // Cambiado a texto para permitir mostrar el mensaje
                            value={numberOfUses}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === "-1") 
                                {
                                    setNumberOfUses("Número de usos");
                                } else 
                                {
                                    const numValue = Number(value);
                                    if (!isNaN(numValue) && numValue >= 0) 
                                    {
                                        setNumberOfUses(numValue);
                                    } 
                                    else 
                                    {
                                        setNumberOfUses(""); // Permitir vaciar el campo
                                    }
                                }
                            }}
                            placeholder="Número de usos"
                            required
                        />
            </div>
        </div>
        {error && <p className="error-message">{error}</p>}
        <div className="form-actions">
          {isEditing && code && code.id !== null && code.id !== -1 && (
            <button type="button" className="delete-event-button" onClick={handleDelete}>
              <i className="fas fa-trash"></i>Borrar
            </button>
          )}
          <div className="form-actions-right">
            <button type="button" onClick={onCancel}>Cancelar</button>   
            <button type="submit" className='save-button'>{isEditing ? 'Guardar' : 'Crear'}</button>    
          </div>   
        </div>
      </form>
      {isModalOpen && (
        <ModalEliminacion
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleConfirmDelete}
        >
          <p>¿Estás seguro de que quieres eliminar este código?</p>
        </ModalEliminacion>
      )}
    </div>
  );
};

export default Codigo;
